import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  UsersIcon,
  XMarkIcon,
  ArrowRightIcon,
  CheckIcon,
  XCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useFamilyStore, Family } from '../../store/familyStore';
import api from '../../api/client';
import { acceptInvite, rejectInvite, withdrawInvite } from '../../api/notifications';
import { useMarkCategoryRead } from '../../hooks/useMarkCategoryRead';

interface CreateFamilyForm {
  name: string;
  description: string;
}

type InviteNotification = {
  id: string;
  title: string;
  body: string;
  created_at: string;
  data?: {
    invite_code?: string;
    family_id?: string;
    family_name?: string;
    inviter_name?: string;
  };
  type?: string;
};

type OutgoingInvite = {
  id: string;
  invite_code: string;
  email?: string | null;
  role: string;
  is_used: boolean;
  expires_at?: string | null;
  created_at: string;
  family_id: string;
  family_name?: string;
};

export default function Families() {
  const navigate = useNavigate();
  const { families, fetchFamilies, createFamily, isLoading } = useFamilyStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [incomingInvites, setIncomingInvites] = useState<InviteNotification[]>([]);
  const [outgoingInvites, setOutgoingInvites] = useState<OutgoingInvite[]>([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const { t } = useTranslation();
  
  // Mark family notifications as read when visiting this page
  useMarkCategoryRead('families');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateFamilyForm>();

  useEffect(() => {
    fetchFamilies();
  }, [fetchFamilies]);

  const onCreateFamily = async (data: CreateFamilyForm) => {
    setIsCreating(true);
    try {
      const family = await createFamily(data.name, data.description);
      toast.success(t('families.toastCreated'));
      setShowCreateModal(false);
      reset();
      navigate(`/families/${family.id}`);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('families.toastCreateFail'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelectFamily = async (family: Family) => {
    await useFamilyStore.getState().fetchFamily(family.id);
    navigate(`/families/${family.id}`);
  };

  const adminFamilies = useMemo(
    () => families.filter((f) => f.my_role === 'owner' || f.my_role === 'admin'),
    [families]
  );

  const loadInvites = useCallback(async () => {
    setLoadingInvites(true);
    try {
      // Use trailing slash to match FastAPI route and avoid proxy 405s
      const notifPromise = api.get('/notifications/', { params: { limit: 100 } });
      const invitesPromises = adminFamilies.map(async (family) => {
        const res = await api.get(`/families/${family.id}/invitations`);
        return (res.data || []).map((inv: any) => ({
          ...inv,
          family_id: family.id,
          family_name: family.name,
        })) as OutgoingInvite[];
      });

      const [notifRes, invitesPerFamily] = await Promise.all([
        notifPromise,
        Promise.all(invitesPromises),
      ]);

      const incoming = (notifRes.data || []).filter(
        (n: any) =>
          (n.type === 'family_invitation' || n.type === 'FAMILY_INVITATION' || n.type === 'family') &&
          n.data?.invite_code &&
          !n.data?.is_used
      );
      const outgoing = invitesPerFamily.flat().filter((inv) => !inv.is_used);

      // Hide used invitations immediately
      setIncomingInvites(incoming.filter((n: any) => !n.data?.is_used));
      setOutgoingInvites(outgoing.filter((inv) => !inv.is_used));
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load invitations');
    } finally {
      setLoadingInvites(false);
    }
  }, [adminFamilies]);

  // Load invites on mount and whenever families list changes (for outgoing invites)
  useEffect(() => {
    loadInvites();
  }, [loadInvites, families.length]);

  const handleAccept = async (code: string) => {
    try {
      setIncomingInvites((prev) => prev.filter((n) => n.data?.invite_code !== code));
      await acceptInvite(code);
      toast.success('Invitation accepted');
      await Promise.all([fetchFamilies(), loadInvites()]);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    }
  };

  const handleReject = async (code: string) => {
    try {
      setIncomingInvites((prev) => prev.filter((n) => n.data?.invite_code !== code));
      await rejectInvite(code);
      toast.success('Invitation rejected');
      await loadInvites();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleWithdraw = async (familyId: string, code: string) => {
    try {
      await withdrawInvite(familyId, code);
      toast.success('Invitation withdrawn');
      await loadInvites();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw');
    }
  };

  // Refresh invitations immediately when notification panel triggers it
  useEffect(() => {
    const refresh = () => {
      fetchFamilies();
      loadInvites();
    };
    window.addEventListener('refreshInvites', refresh);
    return () => window.removeEventListener('refreshInvites', refresh);
  }, [fetchFamilies, loadInvites]);

  const roleColors: Record<string, string> = {
    owner: 'badge-primary',
    admin: 'badge-warning',
    member: 'badge-success',
    child: 'bg-cyan-500/20 text-cyan-300',
    guest: 'bg-surface-500/20 text-surface-300',
  };

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('families.title')}</h1>
          <p className="text-surface-400">{t('families.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-primary-500 hover:bg-primary-600 text-xs text-white transition-colors"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{t('families.create')}</span>
        </button>
      </div>

      {/* Pending invitations - only show if there are any */}
      {(incomingInvites.length > 0 || outgoingInvites.length > 0 || loadingInvites) && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-white">Pending invitations</h2>
              <p className="text-surface-400 text-sm">
                Requests you received and invites you sent to others.
              </p>
            </div>
            {loadingInvites && (
              <div className="w-6 h-6 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {incomingInvites.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-surface-300 uppercase tracking-wide">
                  Requests for you
                </h3>
                {incomingInvites.map((n) => {
                  const code = n.data?.invite_code;
                  return (
                    <div
                      key={n.id}
                      className="p-3 rounded-lg bg-surface-800 border border-surface-700 space-y-1"
                    >
                      <div className="text-white font-semibold">
                        {n.data?.family_name || 'Family invitation'}
                      </div>
                      <div className="text-surface-300 text-sm">{n.body}</div>
                      <div className="flex gap-2 pt-2">
                        <button className="btn-primary btn-sm" onClick={() => code && handleAccept(code)}>
                          <CheckIcon className="w-4 h-4" /> Accept
                        </button>
                        <button className="btn-secondary btn-sm" onClick={() => code && handleReject(code)}>
                          <XCircleIcon className="w-4 h-4" /> Reject
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {outgoingInvites.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-surface-300 uppercase tracking-wide">
                  Invites you sent
                </h3>
                {outgoingInvites.map((inv) => (
                  <div
                    key={`${inv.family_id}-${inv.invite_code}`}
                    className="p-3 rounded-lg bg-surface-800 border border-surface-700 space-y-1"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-white font-semibold">
                          {inv.family_name || 'Family'} • {inv.email || 'link only'}
                        </div>
                        <div className="text-xs text-surface-400">
                          Role: {inv.role} • Code: {inv.invite_code.slice(0, 6)}…
                        </div>
                      </div>
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => handleWithdraw(inv.family_id, inv.invite_code)}
                      >
                        <TrashIcon className="w-4 h-4" />
                        Withdraw
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Families List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
        </div>
      ) : families.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card text-center py-16"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center">
            <UsersIcon className="w-10 h-10 text-primary-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">{t('families.noneTitle')}</h2>
          <p className="text-surface-400 mb-6 max-w-sm mx-auto">
            {t('families.noneDesc')}
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary inline-flex"
          >
            <PlusIcon className="w-5 h-5" />
            <span>{t('families.create')}</span>
          </button>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid gap-4"
        >
          {families.map((family, index) => (
            <motion.div
              key={family.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <button
                onClick={() => handleSelectFamily(family)}
                className="card-interactive w-full text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-2xl shrink-0">
                    🏠
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-white truncate">
                        {family.name}
                      </h3>
                      <span className={roleColors[family.my_role]}>
                        {family.my_role}
                      </span>
                    </div>
                    <p className="text-surface-400 text-sm">
                      {t('families.membersCount', { count: family.member_count })}
                      {family.description && ` • ${family.description}`}
                    </p>
                  </div>
                  <ArrowRightIcon className="w-5 h-5 text-surface-400" />
                </div>
              </button>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Create Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-overlay"
              onClick={() => setShowCreateModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 50 }}
              className="modal-content"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">{t('families.create')}</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="btn-icon"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onCreateFamily)} className="space-y-4">
                <div className="input-group">
                  <label className="input-label">{t('families.nameLabel')}</label>
                  <input
                    type="text"
                    className="input"
                    placeholder={t('families.nameLabel')}
                    {...register('name', { required: t('families.nameLabel') })}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-400">{errors.name.message}</p>
                  )}
                </div>

                <div className="input-group">
                  <label className="input-label">{t('families.descLabel')}</label>
                  <textarea
                    className="input resize-none"
                    rows={3}
                    placeholder={t('families.descPlaceholder')}
                    {...register('description')}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="btn-secondary flex-1"
                  >
                    {t('families.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="btn-primary flex-1"
                  >
                    {isCreating ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      t('families.createCta')
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

